import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Post } from './dashboardSlice';

// Single canonical Post shape lives in dashboardSlice. Re-exported here
// so old `import { Post } from '@/redux/slices/postSlice'` paths still
// resolve, and PostCard can render the same data on either surface.
export type { Post };

interface PostState {
  currentPost: Post | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: PostState = {
  currentPost: null,
  isLoading: false,
  error: null,
};

const postSlice = createSlice({
  name: 'post',
  initialState,
  reducers: {
    setCurrentPost: (state, action: PayloadAction<Post>) => {
      state.currentPost = action.payload;
      state.isLoading = false;
      state.error = null;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    clearCurrentPost: (state) => {
      state.currentPost = null;
      state.isLoading = false;
      state.error = null;
    },

    updatePostInteraction: (state, action: PayloadAction<{ liked: boolean; disliked: boolean }>) => {
      if (state.currentPost && state.currentPost.userInteraction) {
        const oldLiked = state.currentPost.userInteraction.liked;
        const oldDisliked = state.currentPost.userInteraction.disliked;
        const newLiked = action.payload.liked;
        const newDisliked = action.payload.disliked;

        // Calculate the like count delta
        let likeDelta = 0;
        if (oldLiked && !newLiked) {
          likeDelta = -1;
        } else if (!oldLiked && newLiked) {
          likeDelta = 1;
        }

        // Calculate the dislike count delta
        let dislikeDelta = 0;
        if (oldDisliked && !newDisliked) {
          dislikeDelta = -1;
        } else if (!oldDisliked && newDisliked) {
          dislikeDelta = 1;
        }

        // Apply the changes
        state.currentPost.stats.likes += likeDelta;
        state.currentPost.stats.dislikes += dislikeDelta;
        state.currentPost.userInteraction.liked = newLiked;
        state.currentPost.userInteraction.disliked = newDisliked;
      }
    },

    updateCommentCount: (state, action: PayloadAction<number>) => {
      if (state.currentPost) {
        state.currentPost.stats.comments += action.payload;
      }
    },

    // Mirror dashboardSlice.updatePostSpotlight so the post-detail page
    // can react to a spotlight purchase from the shared PostCard.
    updateCurrentPostSpotlight: (state, action: PayloadAction<{ postId: string; durationHours: number }>) => {
      const { postId, durationHours } = action.payload;
      if (state.currentPost && state.currentPost.id === postId) {
        const now = new Date();
        const endTime = new Date(now.getTime() + durationHours * 60 * 60 * 1000);
        state.currentPost.spotlight = {
          startTime: now.toISOString(),
          endTime: endTime.toISOString(),
          cost: '0',
          token: 'KLYX',
        };
      }
    },

    // Mirror dashboardSlice.updatePostContent so the post-detail
    // page's PostCard re-renders after an owner edit (#292) without
    // a refetch.
    updateCurrentPostContent: (state, action: PayloadAction<{
      postId: string;
      body?: string;
      mediaUrl?: string | null;
      previewMode?: 'none' | 'char_limit' | 'text_only' | null;
      previewCharLimit?: number | null;
      isAiGenerated?: boolean;
      updatedAt?: string;
    }>) => {
      const { postId, body, mediaUrl, previewMode, previewCharLimit, isAiGenerated, updatedAt } = action.payload;
      if (!state.currentPost || state.currentPost.id !== postId) return;
      if (body !== undefined) state.currentPost.body = body;
      if (mediaUrl !== undefined) state.currentPost.mediaUrl = mediaUrl ?? '';
      if (previewMode !== undefined) state.currentPost.previewMode = previewMode;
      if (previewCharLimit !== undefined) state.currentPost.previewCharLimit = previewCharLimit;
      if (isAiGenerated !== undefined) state.currentPost.isAiGenerated = isAiGenerated;
      state.currentPost.updatedAt = updatedAt ?? new Date().toISOString();
    },
  },
});

export const {
  setCurrentPost,
  setLoading,
  setError,
  clearError,
  clearCurrentPost,
  updatePostInteraction,
  updateCommentCount,
  updateCurrentPostSpotlight,
  updateCurrentPostContent,
} = postSlice.actions;

export default postSlice.reducer;
